<template>
  <v-container fluid>
    <v-data-table
      dense
      :headers="headers"
      :items="items"
      item-key="id"
      :loading="tableLoading"
      class="elevation-0"
      checkbox-color="deep-purple"
      hide-default-footer
    >
      <template v-slot:item.type="{ item }">
        <v-chip x-small :color="typeColor(item)" dark>
          {{ getUserTypeLabel(item) }}
        </v-chip>
      </template>

      <template v-slot:item.assignment="{ item }">
        <div v-if="getAssignment(item)" class="d-flex align-center">
          <img
            v-if="item.country && item.country.iso_code"
            :src="`${flagsPath}${item.country.iso_code}.png`"
            alt="flag"
            width="28"
            height="20"
            class="mr-1"
          />
          <span>{{ getAssignment(item) }}</span>
        </div>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.state="{ item }">
        <span v-if="item.active" class="green--text font-weight-bold">
          {{ $t('students.active') }}
        </span>
        <span v-else class="orange--text darken-2 font-weight-bold">
          {{ $t('students.inactive') }}
        </span>
      </template>

      <template v-slot:item.edit="{ item }">
        <router-link :to="getEditRoute(item)">
          <v-icon small>mdi-pencil-outline</v-icon>
        </router-link>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          :title="$t('users.impersonate')"
          @click="handleImpersonate(item)"
          :loading="loadingUserId === item.id"
        >
          <v-icon small>mdi-account-switch</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <div class="text-center pt-2">
      <v-pagination
        @input="setValue($event)"
        :length="length"
        :value="value"
        circle
        @next="next"
        @previous="previous"
        :disabled="length < 1"
        :total-visible="7"
      />
    </div>

    <!-- App selector modal (teachers / directive admins) -->
    <general-modal-container v-if="isImpersonateModalOpen" @onClose="closeImpersonateModal">
      <impersonate-app-selector-modal
        :user-name="impersonateUserLabel"
        @onSelect="handleImpersonateAppSelect"
        @onCancel="closeImpersonateModal"
      />
    </general-modal-container>
  </v-container>
</template>

<script>
import { FLAGS_URL } from '../../constants/data';
import LoadingMixin from '../../mixins/loading';
import snackbarMixin from '../../mixins/snackbar';
import impersonationService from '../../services/impersonation.service';
import GeneralModalContainer from '../core/modals/GeneralModalContainer.vue';
import ImpersonateAppSelectorModal from '../teachers/modals/ImpersonateAppSelectorModal.vue';
import {
  buildImpersonationLabel,
  buildImpersonationPayload,
  getImpersonationErrorMessage,
} from '../../utils/impersonation';

// Numeric userType values matching the UserType enum in the backend
const USER_TYPE_READER = 0;
const USER_TYPE_TEACHER = 2;
const USER_TYPE_DIRECTIVE = 3;
const USER_TYPE_DISTRICT_ADMIN = 7;
const USER_TYPE_CAMPUS_GROUP_ADMIN = 8;

// Teacher and directive show the app-selector modal (Teachers App or DataSystem)
const APP_SELECTOR_TYPES = new Set([USER_TYPE_TEACHER, USER_TYPE_DIRECTIVE]);

// District and campus-group admins go directly to DataSystem (no modal)
const DATASYSTEM_DIRECT_TYPES = new Set([USER_TYPE_DISTRICT_ADMIN, USER_TYPE_CAMPUS_GROUP_ADMIN]);

export default {
  name: 'UsersDataTable',
  components: {
    GeneralModalContainer,
    ImpersonateAppSelectorModal,
  },
  props: {
    items: { type: Array, default: () => [] },
    tableLoading: { type: Boolean, default: false },
    value: { type: [String, Number], default: 1 },
    length: { type: [String, Number], default: 1 },
  },
  data: () => ({
    flagsPath: `${FLAGS_URL}countries_flags/`,
    isImpersonateModalOpen: false,
    userToImpersonate: null,
    loadingUserId: null,
  }),
  computed: {
    headers() {
      return [
        { text: 'ID', value: 'id', width: 70 },
        { text: this.$t('students.firstname'), value: 'firstname' },
        { text: this.$t('students.lastname'), value: 'lastname' },
        { text: 'Email / Usuario', value: 'email', sortable: false },
        { text: this.$t('users.user_type'), value: 'type', sortable: false },
        { text: this.$t('students.state'), value: 'state', sortable: false },
        { text: this.$t('users.assignment'), value: 'assignment', sortable: false },
        {
          text: this.$t('students.edit'), value: 'edit', sortable: false, align: 'center',
        },
        {
          text: this.$t('users.impersonate'),
          value: 'actions',
          sortable: false,
          align: 'center',
        },
      ];
    },
    impersonateUserLabel() {
      return this.userToImpersonate ? buildImpersonationLabel(this.userToImpersonate) : '';
    },
  },
  methods: {
    /**
     * Returns a human-readable label for the user's type.
     * Keyed by the numeric UserType enum values from the backend.
     */
    getUserTypeLabel(item) {
      const typeMap = {
        0: this.$t('users.type_student'), // READER
        2: this.$t('users.type_teacher'), // TEACHER
        3: this.$t('users.type_directive'), // DIRECTIVE
        7: this.$t('users.type_district_admin'), // DISTRICT_ADMIN
        8: this.$t('users.type_campus_group_admin'), // CAMPUS_GROUP_ADMIN
      };
      return typeMap[item.userType] ?? this.$t('users.type_unknown');
    },

    typeColor(item) {
      const colorMap = {
        0: 'teal', // READER
        2: 'deep-purple', // TEACHER
        3: 'indigo', // DIRECTIVE
        7: 'blue darken-2', // DISTRICT_ADMIN
        8: 'cyan darken-2', // CAMPUS_GROUP_ADMIN
      };
      return colorMap[item.userType] ?? 'grey';
    },

    /**
     * Returns the router path to the edit view for the given user,
     * based on the numeric userType enum value.
     */
    getEditRoute(item) {
      const routeMap = {
        0: `/edit/students/${item.id}`,
        2: `/edit/teachers/${item.id}`,
        3: `/edit/directive-admins/${item.id}`,
        7: `/edit/district-admins/${item.id}`,
        8: `/edit/campus-group-admins/${item.id}`,
      };
      return routeMap[item.userType] ?? '/dashboard/users';
    },

    /**
     * Returns the assignment string for the user:
     * school name for students/teachers/directives, group name for admins.
     */
    getAssignment(item) {
      return item.school?.name
        || item.schoolGroup?.name
        || item.school_group?.name
        || item.districtName
        || item.campusGroupName
        || null;
    },

    /**
     * Returns true when the user type requires the app selector modal
     * (teacher and directive can access both Teachers App and DataSystem).
     */
    needsAppSelector(item) {
      return APP_SELECTOR_TYPES.has(item.userType);
    },

    handleImpersonate(user) {
      const payload = buildImpersonationPayload(user);
      if (!payload) {
        this.snackbarError(this.$t('users.action_error'));
        return;
      }
      if (this.needsAppSelector(user)) {
        // Teacher / Directive → show modal to pick Teachers App or DataSystem
        this.userToImpersonate = user;
        this.isImpersonateModalOpen = true;
      } else if (DATASYSTEM_DIRECT_TYPES.has(user.userType)) {
        // District Admin / Campus Group Admin → DataSystem directly, no modal
        this.impersonateWithApp(user, false);
      } else {
        // Student (and any other type) → Teachers App directly
        this.impersonateDirect(user);
      }
    },

    closeImpersonateModal() {
      this.isImpersonateModalOpen = false;
      this.userToImpersonate = null;
    },

    async handleImpersonateAppSelect(app) {
      const user = this.userToImpersonate;
      this.closeImpersonateModal();
      await this.impersonateWithApp(user, app === 'teachers');
    },

    async impersonateDirect(user) {
      const payload = buildImpersonationPayload(user);
      if (!payload) {
        this.snackbarError(this.$t('users.action_error'));
        return;
      }
      this.loadingUserId = user.id;
      this.loader();
      try {
        const response = await impersonationService.impersonate(payload);
        const redirectURL = response?.content?.redirectURL;
        if (!redirectURL) {
          throw new Error(String(response?.content ?? this.$t('users.action_error')));
        }
        const label = buildImpersonationLabel(user);
        this.snackbarSuccess(`${this.$t('users.impersonate_opening')} ${label}`.trim());
        window.open(redirectURL, '_blank');
      } catch (error) {
        this.snackbarError(getImpersonationErrorMessage(error, this.$t('users.action_error')));
      } finally {
        this.loadingUserId = null;
        this.loader();
      }
    },

    async impersonateWithApp(user, useTeachersApp) {
      const payload = buildImpersonationPayload(user);
      if (!payload) {
        this.snackbarError(this.$t('users.action_error'));
        return;
      }
      this.loadingUserId = user.id;
      this.loader();
      try {
        if (useTeachersApp) {
          const response = await impersonationService.impersonate(payload);
          const redirectURL = response?.content?.redirectURL;
          if (!redirectURL) {
            throw new Error(String(response?.content ?? this.$t('users.action_error')));
          }
          window.open(redirectURL, '_blank');
        } else {
          const response = await impersonationService.impersonateForDataSystem(payload);
          if (!response?.redirectURL) {
            throw new Error(this.$t('users.action_error'));
          }
          window.open(response.redirectURL, '_blank');
        }
        const label = buildImpersonationLabel(user);
        this.snackbarSuccess(`${this.$t('users.impersonate_opening')} ${label}`.trim());
      } catch (error) {
        this.snackbarError(getImpersonationErrorMessage(error, this.$t('users.action_error')));
      } finally {
        this.loadingUserId = null;
        this.loader();
      }
    },

    next(page) {
      this.$emit('next', page);
    },
    previous(page) {
      this.$emit('previous', page);
    },
    setValue(value) {
      this.$emit('input', value);
    },
  },
  mixins: [LoadingMixin, snackbarMixin],
};
</script>
